import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormulaBuilder } from '@/components/FormulaBuilder';
import { FormulaScopeHint } from '@/components/semantic-models/FormulaScopeHint';
import { Plus } from 'lucide-react';

/**
 * Add Calculated Field dialog — extracted from SemanticModelsPage.
 * Entity-level calculation, scoped to the currently selected entity.
 */
export function CalcFieldDialog({
  open, onOpenChange,
  selectedEntityData,
  calcFieldName, setCalcFieldName,
  calcFieldDesc, setCalcFieldDesc,
  calcFieldFormula, setCalcFieldFormula,
  calcCursorPos, setCalcCursorPos,
  onCreate,
  onAiGenerate,
  onResetForm,
  modelId,
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { onOpenChange(v); if (!v && onResetForm) onResetForm(); }}
    >
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle className="font-heading">Add Calculated Measure</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 -mt-1 mb-1">
          <Badge className="text-[9px] bg-emerald-500/10 text-emerald-300 border-emerald-500/15">Entity Calculation</Badge>
          <span className="text-[10px] text-muted-foreground">Belongs to {selectedEntityData?.name || 'selected entity'}</span>
        </div>
        <div className="space-y-3 mt-1 flex-1 overflow-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Field Name</Label>
              <Input
                value={calcFieldName}
                onChange={e => setCalcFieldName(e.target.value)}
                placeholder="e.g. Net Premium"
                className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm text-foreground"
                data-testid="calc-field-name"
              />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Description</Label>
              <Input
                value={calcFieldDesc}
                onChange={e => setCalcFieldDesc(e.target.value)}
                placeholder="What this calculation represents"
                className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm text-foreground"
              />
            </div>
          </div>
          <FormulaBuilder
            formula={calcFieldFormula}
            onChange={setCalcFieldFormula}
            cursorPos={calcCursorPos}
            onCursorChange={setCalcCursorPos}
            fields={selectedEntityData ? (selectedEntityData.attributes || []).map(a => ({ ...a, entityName: selectedEntityData.name })) : []}
            onAiGenerate={onAiGenerate}
            nameValue={calcFieldName}
          />
          <FormulaScopeHint modelId={modelId} formula={calcFieldFormula} />
        </div>
        <div className="pt-3 border-t border-border/30 mt-2">
          <Button
            onClick={onCreate}
            disabled={!calcFieldName.trim() || !calcFieldFormula.trim()}
            className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95"
            data-testid="confirm-calc-field"
          >
            <Plus className="w-4 h-4 mr-2" />Create Calculated Measure
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
